"use client"

import React from 'react';
import { Button } from "@root/server/client/esweb/components/ui/button"
import { Calendar } from "@root/server/client/esweb/components/ui/calendar"
import { Popover, PopoverContent, PopoverTrigger } from "@root/server/client/esweb/components/ui/popover"
import { CalendarIcon, X } from "lucide-react"
import { format } from "date-fns"
import { cs } from "date-fns/locale"
import { cn } from "@root/server/client/esweb/lib/utils"
import { type Control, Controller } from "react-hook-form"
import {Field, FieldError, FieldLabel} from "@root/server/client/esweb/components/ui/field";

interface DatePickerFieldProps {
  control: Control<any>
  name: string
  label: string
  placeholder?: string
  multiple?: boolean
  error?: string
}

export function DatePickerField({
  control,
  name,
  label,
  placeholder = "Vyberte termíny",
  multiple = true,
  error,
}: DatePickerFieldProps) {
  return (
    <Controller
      control={control}
      name={name}
      render={({ field, fieldState }) => {
        const selectedDates = multiple
          ? Array.isArray(field.value)
            ? field.value
            : []
          : field.value
            ? [field.value]
            : []

        const handleDateSelect = (dates: Date | Date[] | undefined) => {
          if (multiple) {
            field.onChange(dates || [])
          } else {
            field.onChange(dates)
          }
        }

        const handleRemoveDate = (dateToRemove: Date) => {
          if (multiple && Array.isArray(field.value)) {
            const newDates = field.value.filter((d: Date) => d.toDateString() !== dateToRemove.toDateString())
            field.onChange(newDates)
          }
        }

        return (
          <Field data-invalid={fieldState.invalid}>
              <FieldLabel>
                  {label}
              </FieldLabel>
            <Popover>
              <PopoverTrigger asChild>
                <Button
                  type="button"
                  variant="outline"
                  className={cn(
                    "w-full justify-start text-left font-normal",
                    !selectedDates.length && "text-muted-foreground",
                  )}
                >
                  <CalendarIcon className="mr-2 h-4 w-4" />
                  {selectedDates.length > 0
                    ? multiple
                      ? `Vybráno ${selectedDates.length} ${selectedDates.length === 1 ? "den" : selectedDates.length < 5 ? "dny" : "dní"}`
                      : format(selectedDates[0], "d. M. yyyy", { locale: cs })
                    : placeholder}
                </Button>
              </PopoverTrigger>
                <PopoverContent className="w-auto p-0" align="start">
                    <Calendar
                        required
                        mode={multiple ? "multiple" : "single"}
                        selected={multiple ? selectedDates : selectedDates[0]}
                        onSelect={handleDateSelect as any}
                        locale={cs}
                        disabled={(date) => date < new Date(new Date().setHours(0, 0, 0, 0))}
                        initialFocus
                    />
              </PopoverContent>
            </Popover>
              {fieldState.invalid && (
                    <FieldError errors={[fieldState.error]} />
                )}
            {multiple && selectedDates.length > 0 && (
              <div className="flex flex-wrap gap-2">
                {selectedDates.map((date: Date, index: number) => (
                  <div
                    key={index}
                    className="inline-flex items-center gap-1 px-2 py-1 bg-primary/10 text-primary rounded-md text-sm"
                  >
                    {format(date, "d. M. yyyy", { locale: cs })}
                    <button
                      type="button"
                      onClick={() => handleRemoveDate(date)}
                      className="hover:bg-primary/20 rounded-full p-0.5"
                    >
                      <X className="h-3 w-3" />
                    </button>
                  </div>
                ))}
              </div>
            )}
          </Field>
        )
      }}
    />
  )
}
